import { sumBy } from 'lodash'
import * as XLSX from 'xlsx'
import { formatDate } from '../../../../../../../helpers/helpers'
import { getAllRegistrationFees } from '../../../../../../../helpers/registrationFees'
import { convertToDate, extractDatePart } from '../../../../../../../helpers/time'
import { getRiderStatus, parseAddress } from '../../eventResultsReportUtils'
import { getRidersByRideTimeByClass } from '../../../../ManageClinicNOtherOperationsTab/components/OrderOfGoTable/data/order-of-go-rider-utils'

import { IEventReviewPublish } from '../../../../../../../models/event-review-publish/event-review-publish.interface'

import { RegistrationFeesType } from '../../../../../../../models/event-fees/event-fees.interface'
import { TEventRegisteredUsers } from '../../../../../../../models/event-registered-users/event-registered-users.interface'
import { IEventInterface } from '../../../../../../../models/events/event.interface'
import { IHorseData } from '../../../../../../../models/horse/horse.interface'
import { IRegistrationFeesInterface } from '../../../../../../../models/registration-fees/registrationFees.interface'
import { IRegistrationByDayInterface } from '../../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IUserInterface } from '../../../../../../../models/users/user.interface'

import {
  CLASS_REPORT_COLUMNS,
  ClassReportRow,
  CUSTOM_QUESTIONS_REPORT_COLUMNS,
  CustomQuestionsRow,
  EnabledOptionsType,
  EVENT_REPORT_FIELDS,
  FEES_REPORT_COLUMNS,
  GUARDIAN_REPORT_COLUMNS,
  GuardianReportRow,
  HORSE_REPORT_COLUMNS,
  HorseReportRow,
  JUDGE_REPORT_COLUMNS,
  JudgeReportRow,
  ONSITE_VENDOR_REPORT_COLUMNS,
  OnsiteVendorReportRow,
  OWNER_REPORT_COLUMNS,
  OwnerReportRow,
  PERSONNEL_REPORT_FIELDS,
  RIDER_REPORT_COLUMNS,
  RiderReportRow,
  SCHEDULE_REPORT_COLUMNS,
  ScheduleReportRow,
  SCORE_REPORT_COLUMNS,
  ScoreReportRow,
  SPONSOR_REPORT_COLUMNS,
  SponsorReportRow,
  STAFF_REPORT_COLUMNS,
  StaffReportRow,
  STALL_REPORT_COLUMNS,
  StallReportRow,
  TEAM_REPORT_COLUMNS,
  TeamReportRow,
  TRAINER_REPORT_COLUMNS,
  TrainerReportRow,
  VIRTUAL_VENDOR_REPORT_COLUMNS,
  VirtualVendorReportRow,
} from './custom-data-export-const'
import {
  calculateEventStats,
  calculateJudgeMetrics,
  checkCertificateStatus,
  convertTo12HourReportFormat,
  formatHorseYearOfBirth,
  getFirstAndLastNames,
  getFormattedUserName,
  getHorseMembershipNumbers,
  getStringValue,
  getTeamMemberNamesByRole,
  getUserDisciplineNumbers,
  hasBeenScored,
} from './customDataExportUtils'
import {
  getGuardianToRidersMap,
  getHorsePaperworkData,
  getPersonnelStats,
  getRegistrationFees,
  getRiderTeamMemberData,
  getRoleIds,
  getStaffRolesData,
  getTeamReportData,
  getTrainerRiderRelationships,
} from './reportDataService'

import { horseBreedList } from '../../../../../../../fakeData/horseBreedList'
import { disciplineData } from '../../../../../../../fakeData/disciplineList'
import { horseMarkingList } from '../../../../../../../fakeData/horseMarkingList'
import { amateurList } from '../../../../../../../fakeData/amateurList'
import { horseHeightList } from '../../../../../../../fakeData/HorseHeightList'
import { zoneList } from '../../../../../../../fakeData/ZoneList'
import { horseColorList } from '../../../../../../../fakeData/horseColorList'
import { CountryList } from '../../../../../../../fakeData/countryList'

export interface ExportData {
  event: IEventInterface | null
  registrationFees: any
  registeredUsersByDay: IRegistrationByDayInterface[] | null
  horses: IHorseData[] | null
  users: IUserInterface[] | null
  eventReviewPublish: IEventReviewPublish | null
  scheduleBy: 'class' | 'rider'
  eventRegisteredUsers: TEventRegisteredUsers[] | null
}

// create a new workbook obj and we will add a sheet for each custom report enabled
export const createWorkbook = (): XLSX.WorkBook => {
  return XLSX.utils.book_new()
}

export const generateExport = async (
  data: ExportData,
  enabledOptions: EnabledOptionsType
): Promise<XLSX.WorkBook> => {
  const workbook = createWorkbook()

  if (enabledOptions.eventReport) {
    await generateEventReport(workbook, data)
  }
  if (enabledOptions.classReport) {
    generateClassReport(workbook, data)
  }

  if (enabledOptions.scoreReport) {
    generateScoreReport(workbook, data)
  }

  if (enabledOptions.scheduleReport) {
    generateScheduleReport(workbook, data)
  }

  if (enabledOptions.stallReport) {
    await generateStallReport(workbook, data)
  }

  if (enabledOptions.feesReport) {
    await generateFeesReport(workbook, data)
  }

  if (enabledOptions.personnelReport) {
    await generatePersonnelReport(workbook, data)
  }

  if (enabledOptions.teamReport) {
    await generateTeamReport(workbook, data)
  }

  if (enabledOptions.questionsReport) {
    await generateCustomQuestionsReport(workbook, data)
  }

  if (enabledOptions.horseReport) {
    await generateHorseReport(workbook, data)
  }

  if (enabledOptions.riderReport) {
    await generateRiderReport(workbook, data)
  }

  if (enabledOptions.ownerReport) {
    await generateOwnerReport(workbook, data)
  }

  if (enabledOptions.trainerReport) {
    await generateTrainerReport(workbook, data)
  }

  if (enabledOptions.guardianReport) {
    await generateGuardianReport(workbook, data)
  }

  if (enabledOptions.judgeReport) {
    generateJudgeReport(workbook, data)
  }

  if (enabledOptions.staffReport) {
    await generateStaffReport(workbook, data)
  }

  if (enabledOptions.sponsorReport) {
    generateSponsorReport(workbook, data)
  }

  if (enabledOptions.onsiteVendorReport) {
    generateOnsiteVendorReport(workbook, data)
  }

  if (enabledOptions.virtualVendorReport) {
    generateVirtualVendorReport(workbook, data)
  }

  return workbook
}

// Generate event report (vertical format)
const generateEventReport = async (workbook: XLSX.WorkBook, data: ExportData): Promise<void> => {
  try {
    const { registeredUsersByDay, registrationFees, eventReviewPublish, event } = data

    const eventId = event?.id || ''

    const eventTickets = eventReviewPublish?.EventTickets?.tickets ?? []

    const eventStats = calculateEventStats(registeredUsersByDay ?? [])

    const personnelStats = await getPersonnelStats(eventId)

    const allClasses = getAllRegistrationFees(registrationFees?.registrationFees || [])

    const wsData: Array<[string, string | number]> = EVENT_REPORT_FIELDS.map((field) => {
      let value: string | number

      switch (field) {
        case 'Competition Date':
          const date = convertToDate(eventReviewPublish?.EventDetails?.competitionStartDate)
          value = date ? extractDatePart(date) : 'Not available'
          break
        case 'Governing Body Comp ID Number':
          const eventDetails = eventReviewPublish?.EventDetails
          const bodyCompId =
            eventDetails && 'bodyCompId' in eventDetails ? eventDetails.bodyCompId : null
          value = Number(bodyCompId) || 'Not specified'
          break
        case 'Number Of Entries':
          value = eventStats.numberOfEntries
          break
        case 'Number Of Scratches':
          value = eventStats.numberOfScratches
          break
        case 'Number That Competed':
          value = eventStats.numberOfCompeted
          break
        case 'Number That Completed':
          value = eventStats.numberOfCompleted
          break

        case 'Prize Money Awarded':
          // calculate prize money awarded
          try {
            value =
              registeredUsersByDay?.reduce((total, entry) => {
                // skip entries without scores or earnings
                if (!entry.score?.earning) return total

                // find the registration fee for this class
                const classId: string = entry.uuid || ''
                const registrationFee = allClasses.find(
                  (fee: RegistrationFeesType) => fee.uuid === classId
                )

                // if both prize money and earning percentage are available, calculate the amount
                if (registrationFee?.prizeMoney) {
                  const dollarAmount =
                    (Number(registrationFee.prizeMoney) * Number(entry.score.earning)) / 100
                  return total + dollarAmount
                }

                return total
              }, 0) ?? 0
          } catch (err) {
            console.error('Error calculating prize money awarded:', err)
            value = 0
          }
          break
        case 'Prize Money Offered':
          try {
            value =
              allClasses.reduce((total: number, fee: RegistrationFeesType) => {
                return total + (Number(fee.prizeMoney) || 0)
              }, 0) || 0
          } catch (err) {
            console.error('Error calculating prize money offered:', err)
            value = 0
          }
          break
        case 'Number Of Spectator Tickets':
          try {
            value = eventTickets.reduce((sum, ticket) => sum + (ticket.sold || 0), 0)
          } catch (err) {
            console.error('Error calculating spectator tickets:', err)
            value = 0
          }
          break

        case 'Number Of Riders':
          value = personnelStats.riders
          break
        case 'Number Of Trainers':
          value = personnelStats.trainers
          break
        case 'Number Of Owners':
          value = personnelStats.owners
          break
        case 'Number Of Guardians':
          value = personnelStats.guardians
          break
        case 'Number Of Show Manager':
          value = personnelStats.showManager
          break
        case 'Number Of Secretary':
          value = personnelStats.secretary
          break
        case 'Number Of Course Designer':
          value = personnelStats.courseDesigners
          break
        case 'Number Of Technical Delegate':
          value = personnelStats.technicalDelegate
          break
        case 'Number Of Judges':
          value = personnelStats.judges
          break
        case 'Number Of Scorers':
          value = personnelStats.scorers
          break
        case 'Number Of Stewards':
          value = personnelStats.stewards
          break
        case 'Number Of Ingates':
          value = personnelStats.ingates
          break
        case 'Number Of Announcers':
          value = personnelStats.announcers
          break
        case 'Number Of Farriers':
          value = personnelStats.farriers
          break
        case 'Number Of Veterinarians':
          value = personnelStats.veterinarians
          break
        case 'Number Of Press':
          value = personnelStats.press
          break
        case 'Number Of Other Staff':
          value = personnelStats.otherStaff
          break
        case 'Count Of All Staff Roles':
          value = personnelStats.allStaff
          break

        case 'Run Status':
          // format the run status counts as a string
          value = Object.entries(eventStats.runStatusCounts)
            .filter(([_, count]) => count > 0)
            .map(([status, count]) => `${status}: ${count}`)
            .join(', ')
          break
        case 'Number Of Sponsors':
          value = 'Coming soon'
          break
        case 'Number Of On Site Vendors':
          value = 'Coming soon'
          break
        case 'Number Of Virtual Vendors':
          value = 'Coming soon'
          break
        case 'Number Of Press Passes':
          value = 'Coming soon'
          break
        default:
          value = 'Not available'
      }

      // ensure value is a string or number
      if (value === null || value === undefined) {
        value = ''
      } else if (typeof value === 'object') {
        value = JSON.stringify(value)
      }

      return [field, value]
    })

    const ws = XLSX.utils.aoa_to_sheet(wsData)

    // set col widths - static 2 columns for vertical report

    ws['!cols'] = [{ wch: 40 }, { wch: 20 }]

    XLSX.utils.book_append_sheet(workbook, ws, 'Event Information')
  } catch (err) {
    console.error('Failed to generate event report: ', err)
    const emptyData = EVENT_REPORT_FIELDS.map((field) => [field, ''])
    const ws = XLSX.utils.aoa_to_sheet(emptyData)

    ws['!cols'] = [{ wch: 40 }, { wch: 20 }]
    XLSX.utils.book_append_sheet(workbook, ws, 'Event Information')
  }
}

// Generate personnel report (vertical format)
const generatePersonnelReport = async (
  workbook: XLSX.WorkBook,
  data: ExportData
): Promise<void> => {
  try {
    const { event } = data
    const eventId = event?.id || ''

    const stats = await getPersonnelStats(eventId)

    const wsData: Array<[string, string | number]> = PERSONNEL_REPORT_FIELDS.map((field) => {
      let value: string | number

      switch (field) {
        case 'Number Of Riders':
          value = stats.riders
          break
        case 'Number Of Trainers':
          value = stats.trainers
          break
        case 'Number Of Owners':
          value = stats.owners
          break
        case 'Number Of Guardians':
          value = stats.guardians
          break
        case 'Number Of Show Managers':
          value = stats.showManager
          break
        case 'Number Of Secretary':
          value = stats.secretary
          break
        case 'Number Of Course Designer':
          value = stats.courseDesigners
          break
        case 'Number Of Technical Delegate':
          value = stats.technicalDelegate
          break
        case 'Number Of Judges':
          value = stats.judges
          break
        case 'Number Of Scorers':
          value = stats.scorers
          break
        case 'Number Of Stewards':
          value = stats.stewards
          break
        case 'Number Of Ingates':
          value = stats.ingates
          break
        case 'Number Of Announcers':
          value = stats.announcers
          break
        case 'Number Of Farriers':
          value = stats.farriers
          break
        case 'Number Of Veterinarians':
          value = stats.veterinarians
          break
        case 'Number Of Press':
          value = stats.press
          break
        case 'Number Of Other Staff':
          value = stats.otherStaff
          break
        case 'Count Of All Staff Roles':
          value = stats.allStaff
          break
        default:
          value = ''
      }

      if (value === null || value === undefined) {
        value = ''
      } else if (typeof value === 'object') {
        value = JSON.stringify(value)
      }

      return [field, value]
    })

    const ws = XLSX.utils.aoa_to_sheet(wsData)

    // static col widths for vertical report
    ws['!cols'] = [{ wch: 40 }, { wch: 20 }]

    XLSX.utils.book_append_sheet(workbook, ws, 'Personnel Information')
  } catch (err) {
    console.error('Failed to generate personnel report: ', err)
    const emptyData = PERSONNEL_REPORT_FIELDS.map((field) => [field, ''])
    const ws = XLSX.utils.aoa_to_sheet(emptyData)
    ws['!cols'] = [{ wch: 40 }, { wch: 20 }]
    XLSX.utils.book_append_sheet(workbook, ws, 'Personnel Information')
  }
}

// Generate class report (horizontal format)
const generateClassReport = (workbook: XLSX.WorkBook, data: ExportData): void => {
  const [
    classNumberCol,
    classTitleCol,
    numEntriesCol,
    numCompetedCol,
    numCompletedCol,
    bonusFeesCol,
    bonusFeesSoldCol,
    prizeMoneyAwardedCol,
    prizeMoneyOfferedCol,
  ] = CLASS_REPORT_COLUMNS

  try {
    const { registrationFees, registeredUsersByDay } = data

    const classes = getAllRegistrationFees(registrationFees?.registrationFees || [])

    const rows = classes.map((classData: RegistrationFeesType) => {
      // find all registrations for this class
      const classRegistrations =
        registeredUsersByDay?.filter((reg) => reg.uuid === classData.uuid) || []

      // calc stats for each class
      const numEntries = classRegistrations.length
      const numCompeted = classRegistrations.filter((reg) => !reg.isScratched).length
      const numCompleted = classRegistrations.filter(
        (reg) => !reg.isScratched && reg.score !== null
      ).length

      // calculate bonus fees collected - actual dollar amount
      const bonusFeesCollected = classRegistrations.reduce((total, entry) => {
        // only count if not scratched and has a qualify fee
        if (entry.isScratched) return total

        // add the qualify fee amount if it exists and is a valid number
        const feeAmount = entry.isQualify ? Number(entry.qualifyFee) : 0
        if (!isNaN(feeAmount)) {
          return total + feeAmount
        }
        return total
      }, 0)

      // count number of bonus fees sold - aggregated by qualifyFeeName
      const bonusFeeNamesCount: Record<string, number> = {}

      classRegistrations.forEach((entry) => {
        // only count if not scratched and has a qualify fee name
        if (entry.isScratched || !entry.qualifyFeeName) return

        // increment count for this fee name
        const feeName = entry.qualifyFeeName
        bonusFeeNamesCount[feeName] = (bonusFeeNamesCount[feeName] || 0) + 1
      })

      // total count of all bonus fees sold
      const bonusFeesSold = Object.values(bonusFeeNamesCount).reduce((a, b) => a + b, 0)

      let prizeMoneyAwarded = 0
      try {
        prizeMoneyAwarded = classRegistrations.reduce((total, entry) => {
          // skip entries without scores or earnings
          if (!entry.score?.earning) return total

          // calc dollar amount based on prize money and earning percentage
          if (classData.prizeMoney) {
            const dollarAmount = (Number(classData.prizeMoney) * Number(entry.score.earning)) / 100
            return total + dollarAmount
          }

          return total
        }, 0)
      } catch (err) {
        console.error(`Error calculating prize money awarded for class ${classData.name}:`, err)
      }

      const record: ClassReportRow = {}
      record[classNumberCol] = classData.order || ''
      record[classTitleCol] = classData.name || ''
      record[numEntriesCol] = numEntries
      record[numCompetedCol] = numCompeted
      record[numCompletedCol] = numCompleted
      record[bonusFeesCol] = bonusFeesCollected
      record[bonusFeesSoldCol] = bonusFeesSold
      record[prizeMoneyAwardedCol] = prizeMoneyAwarded
      record[prizeMoneyOfferedCol] = Number(classData.prizeMoney) || 0

      return record
    })

    const sortedRows = rows.sort((a: ClassReportRow, b: ClassReportRow) => {
      // try compare as numbers
      const aNum = Number(a[classNumberCol])
      const bNum = Number(b[classNumberCol])

      // if valid numbers, compare them numerically
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum
      }

      // otherwise compare as strings
      return String(a[classNumberCol]).localeCompare(String(b[classNumberCol]))
    })

    const ws = XLSX.utils.json_to_sheet(sortedRows, { header: CLASS_REPORT_COLUMNS })

    const columnWidths = CLASS_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...rows.map((record: ClassReportRow) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          15
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Class Information')
  } catch (err) {
    console.error('Failed to generate score report: ', err)
    const ws = XLSX.utils.json_to_sheet([], { header: CLASS_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Class Information')
  }
}

// Generate score report (horizontal format)
const generateScoreReport = (workbook: XLSX.WorkBook, data: ExportData): void => {
  const [
    classNumberCol,
    classTitleCol,
    riderNameCol,
    horseNameCol,
    backNumberCol,
    scoreTotalCol,
    percentageTotalCol,
    scoreLevelCol,
    placingCol,
    numEntriesCol,
    numCompetedCol,
    numCompletedCol,
    prizeMoneyOfferedCol,
    prizeMoneyAwardedCol,
    rideStatusCol,
  ] = SCORE_REPORT_COLUMNS

  const { registrationFees, registeredUsersByDay } = data

  try {
    if (!registeredUsersByDay || registeredUsersByDay.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: SCORE_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Score Information')
      return
    }

    const allClasses = getAllRegistrationFees(registrationFees?.registrationFees || [])

    const filteredRegistrations = registeredUsersByDay.filter((registration) => {
      const hasMatchingFee = allClasses.some(
        (fee: RegistrationFeesType) => fee.uuid === registration.uuid
      )

      return hasMatchingFee
    })

    // pre calc class stats - avoid repeated calculations as multiple entries in same class
    const classStatsMap: Record<string, ReturnType<typeof calculateEventStats>> = {}

    // group registrations by class uuid
    const registrationsByClass: Record<string, IRegistrationByDayInterface[]> = {}

    filteredRegistrations.forEach((registration) => {
      const classUuid = registration.uuid
      if (!classUuid) return

      if (!registrationsByClass[classUuid]) {
        registrationsByClass[classUuid] = []
      }

      registrationsByClass[classUuid].push(registration)
    })

    // calculate stats for each class once
    Object.entries(registrationsByClass).forEach(([classUuid, classRegistrations]) => {
      classStatsMap[classUuid] = calculateEventStats(classRegistrations)
    })

    const rows = filteredRegistrations.map((registration) => {
      const classData = allClasses.find(
        (fee: RegistrationFeesType) => fee.uuid === registration.uuid
      ) as RegistrationFeesType | undefined

      const classUuid = registration.uuid || ''

      const classStats = classStatsMap[classUuid] || {
        numberOfEntries: 0,
        numberOfScratches: 0,
        numberOfCompeted: 0,
        numberOfCompleted: 0,
        runStatusCounts: {},
      }

      // calc prize money for this entry
      let prizeMoneyAwarded = 0
      if (registration.score?.earning && classData?.prizeMoney) {
        prizeMoneyAwarded =
          (Number(classData.prizeMoney) * Number(registration.score.earning)) / 100
      }

      const riderName = registration ? `${registration.riderName || ''}` : 'No Rider'

      const horseName = registration ? `${registration.horseName || ''}` : 'No Horse'

      const backNumber = registration.backNumber ?? 0

      const rank = registration.score?.rank || ''

      // get percentage from score if available
      let scorePercentage = ''
      if (registration.score?.judges && registration.score.judges.length > 0) {
        const max = sumBy(registration.score.judges[0].sections, (section) => section.max)
        if (max && registration.score.totalPoints) {
          scorePercentage = ((Number(registration.score.totalPoints) / max) * 100).toFixed(3)
        }
      }

      // row object
      const record: ScoreReportRow = {}
      record[classNumberCol] = classData?.order || ''
      record[classTitleCol] = classData?.name || ''
      record[riderNameCol] = riderName
      record[horseNameCol] = horseName
      record[backNumberCol] = backNumber
      record[scoreTotalCol] = registration.score?.totalPoints || ''
      record[percentageTotalCol] = scorePercentage ? Number(scorePercentage) : ''
      record[scoreLevelCol] = registration.score?.sheet?.name || ''
      record[placingCol] = rank
      record[numEntriesCol] = classStats.numberOfEntries
      record[numCompetedCol] = classStats.numberOfCompeted
      record[numCompletedCol] = classStats.numberOfCompleted
      record[prizeMoneyAwardedCol] = prizeMoneyAwarded
      record[prizeMoneyOfferedCol] = Number(classData?.prizeMoney) || 0
      record[rideStatusCol] = getRiderStatus(registration)

      return record
    })

    rows.sort((a: ScoreReportRow, b: ScoreReportRow) => {
      // sort by class number
      const aClassNum = Number(a[classNumberCol])
      const bClassNum = Number(b[classNumberCol])

      //class numbers different, sort by class number
      if (aClassNum !== bClassNum) {
        return aClassNum - bClassNum
      }

      // class numbers are the same -  sort by placing - empty or NaN to infin to rank below valid numbers
      const aPlacing = Number(a[placingCol]) || Infinity
      const bPlacing = Number(b[placingCol]) || Infinity

      return aPlacing - bPlacing
    })

    const ws = XLSX.utils.json_to_sheet(rows, { header: SCORE_REPORT_COLUMNS })

    const columnWidths = SCORE_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...rows.map((record: ScoreReportRow) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          15
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Score Information')
  } catch (err) {
    console.error('Failed to generate score report: ', err)
    const ws = XLSX.utils.json_to_sheet([], { header: SCORE_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Score Information')
  }
}

// Generate schedule report (horizontal format)
const generateScheduleReport = (workbook: XLSX.WorkBook, data: ExportData): void => {
  try {
    const [
      classDateCol,
      facilityCol,
      classNumberCol,
      classTitleCol,
      orderOfGoCol,
      rideTimeCol,
      riderNameCol,
      horseNameCol,
      backNumberCol,
      rideStatusCol,
    ] = SCHEDULE_REPORT_COLUMNS

    const { registrationFees, registeredUsersByDay, scheduleBy, event } = data || {}

    if (!registeredUsersByDay || registeredUsersByDay.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: SCHEDULE_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Schedule Information')
      return
    }

    const allClasses = getAllRegistrationFees(registrationFees?.registrationFees || [])

    const filteredRegistrations = registeredUsersByDay.filter((registration) => {
      const hasMatchingFee = allClasses.some(
        (fee: RegistrationFeesType) => fee.uuid === registration.uuid
      )

      return hasMatchingFee
    })

    const classRideTimes = new Map()

    if (scheduleBy === 'class') {
      // group registrations by class
      const registrationsByClass: Record<string, any[]> = {}

      filteredRegistrations.forEach((registration) => {
        const classId = registration.uuid
        if (!classId) {
          return
        }

        if (!registrationsByClass[classId]) {
          registrationsByClass[classId] = []
        }
        registrationsByClass[classId].push(registration)
      })

      // calc ride time for each registration in each class
      Object.entries(registrationsByClass).forEach(([classId, classRegistrations]) => {
        const classData = allClasses.find((fee: RegistrationFeesType) => fee.uuid === classId)

        if (!classData) {
          return
        }

        try {
          // sort by order property for consistent ordering
          const sortedRegistrations = [...classRegistrations].sort((a, b) => {
            // skip scratched riders in order calculations
            if (a.isScratched && !b.isScratched) return 1
            if (!a.isScratched && b.isScratched) return -1

            const aOrder = Number(a.order) || Infinity
            const bOrder = Number(b.order) || Infinity
            return aOrder - bOrder
          })

          // calc ride time for each registration
          sortedRegistrations.forEach((registration) => {
            if (classData.increment) {
              try {
                const ridersByRideTimeByClass = getRidersByRideTimeByClass(
                  [
                    ...sortedRegistrations.filter((item) => item.uuid === classData.uuid),
                    ...(event?.breaks?.filter(
                      (item) => item.registrationByDayUuid === classData.uuid
                    ) || []),
                  ],
                  classData,
                  classData.increment
                )
                const current = ridersByRideTimeByClass.find((item) => item.id === registration.id)
                classRideTimes.set(
                  registration.id,
                  current?.orderOfGoScratched ? 'Dropped' : current?.rideTimeScheduleByClass
                )
              } catch (err) {
                console.error('Error calculating ride time:', err)
              }
            }
          })
        } catch (err) {
          console.error(`Error processing class ${classId}:`, err)
        }
      })
    }

    const rows = filteredRegistrations.map((registration) => {
      try {
        // find the class data for registration
        const classData = allClasses.find(
          (fee: RegistrationFeesType) => fee.uuid === registration.uuid
        ) as RegistrationFeesType | undefined

        const riderName = registration.riderName || 'No Rider'
        const horseName = registration.horseName || 'No Horse'
        const backNumber = registration.backNumber ?? 0

        // determine ride time based on schedule mode
        let rideTime = 'Not set'

        if (scheduleBy === 'rider') {
          rideTime = convertTo12HourReportFormat(registration.rideTime)
        } else if (scheduleBy === 'class') {
          // use calculated class based ride time from map
          const calculatedTime = classRideTimes.get(registration.id)
          if (calculatedTime) {
            rideTime = calculatedTime
          }
        }

        const facility = classData?.location || ''

        const orderOfGo = registration.isScratched ? 'Scratched' : registration.order

        // get class date from the class fee startDate
        let classDate = ''
        if (classData?.startDate) {
          try {
            classDate = extractDatePart(classData.startDate)
          } catch (err) {
            console.error('Error parsing class start date:', err)
          }
        }

        const record: ScheduleReportRow = {}
        record[classDateCol] = classDate
        record[facilityCol] = facility
        record[classNumberCol] = classData?.order || ''
        record[classTitleCol] = classData?.name || ''
        record[orderOfGoCol] = orderOfGo
        record[rideTimeCol] = rideTime ? rideTime : 'Not set'
        record[riderNameCol] = riderName
        record[horseNameCol] = horseName
        record[backNumberCol] = backNumber
        record[rideStatusCol] = getRiderStatus(registration)

        return record
      } catch (err) {
        console.error(
          `Error processing schedule entry for ${registration.registrationByDayName}:`,
          err
        )
        // return a partial row w/ error
        const record: Record<string, any> = {}
        record[classDateCol] = 'Error'
        record[classNumberCol] = registration.registrationByDayName || 'Unknown'
        record[classTitleCol] = 'Error processing entry'
        record[rideStatusCol] = 'Error'
        return record
      }
    })

    if (rows.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: SCHEDULE_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Schedule Information')
      return
    }

    // sort rows by class date, then class number, then order of go
    rows.sort((a, b) => {
      // first sort by class date
      const aClassDate: string = a[classDateCol].toString()
      const bClassDate: string = b[classDateCol].toString()
      const dateComparison = aClassDate.localeCompare(bClassDate)

      if (dateComparison !== 0) {
        return dateComparison
      }

      // then sort by class number
      const aClassNum: number = Number(a[classNumberCol]) || 0
      const bClassNum: number = Number(b[classNumberCol]) || 0

      if (aClassNum !== bClassNum) {
        return aClassNum - bClassNum
      }

      // finally sort by order of go
      const aOrderOfGo = Number(a[orderOfGoCol]) || Infinity
      const bOrderOfGo = Number(b[orderOfGoCol]) || Infinity
      return aOrderOfGo - bOrderOfGo
    })

    const ws = XLSX.utils.json_to_sheet(rows, { header: SCHEDULE_REPORT_COLUMNS })

    const columnWidths = SCHEDULE_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...rows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          15
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Schedule Information')
  } catch (err) {
    console.error('Failed to generate schedule report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: SCHEDULE_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Schedule Information')
  }
}

// Generate fees report (vertical format)
const generateFeesReport = async (workbook: XLSX.WorkBook, data: ExportData): Promise<void> => {
  try {
    const { event } = data
    const eventId = event?.id || ''

    let registrationFees: IRegistrationFeesInterface[] = []

    try {
      if (eventId) {
        registrationFees = await getRegistrationFees(eventId)
      }
    } catch (error) {
      console.error('Error fetching registration fees:', error)
    }

    if (!registrationFees || !Array.isArray(registrationFees) || registrationFees.length === 0) {
      const emptyData = FEES_REPORT_COLUMNS.map((column) => [column, ''])
      const ws = XLSX.utils.aoa_to_sheet(emptyData)
      ws['!cols'] = [{ wch: 40 }, { wch: 20 }]
      XLSX.utils.book_append_sheet(workbook, ws, 'Fees Information')
      return
    }

    // group fees by title
    const feeCounts: Record<string, number> = {}

    registrationFees.forEach((fee: IRegistrationFeesInterface) => {
      // skip null or undefined fee titles if any
      if (!fee.feesTitle) return

      // only count non-scratched items
      if (!fee.isScratched) {
        const title = fee.feesTitle

        if (!feeCounts[title]) {
          feeCounts[title] = 0
        }

        // increment the count by selectedUnitsCount or 1 if no selectedUnitsCount on fee item for some reason
        feeCounts[title] += fee.selectedUnitsCount || 1
      }
    })

    const reportData: Array<[string, string | number]> = []

    Object.entries(feeCounts)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([title, count]) => {
        reportData.push([title, count])
      })

    reportData.push(['', ''])

    const totalSold = Object.values(feeCounts).reduce((sum, count) => sum + count, 0)
    reportData.push(['Total Items Sold', totalSold])

    const ws = XLSX.utils.aoa_to_sheet(reportData)
    ws['!cols'] = [{ wch: 40 }, { wch: 20 }]
    XLSX.utils.book_append_sheet(workbook, ws, 'Fees Information')
  } catch (err) {
    console.error('Failed to generate fees report: ', err)
    const emptyData = FEES_REPORT_COLUMNS.map((column) => [column, ''])
    const ws = XLSX.utils.aoa_to_sheet(emptyData)
    ws['!cols'] = [{ wch: 40 }, { wch: 20 }]
    XLSX.utils.book_append_sheet(workbook, ws, 'Fees Information')
  }
}

// Generate stall report (horizontal format)
const generateStallReport = async (workbook: XLSX.WorkBook, data: ExportData): Promise<void> => {
  try {
    const [riderNameCol, horsesCol, stallCol, beddingCol, feedCol, totalItemsCol] =
      STALL_REPORT_COLUMNS

    const { horses, users, event } = data || {}

    let feeItems: any[] = []

    if (event?.id) {
      try {
        feeItems = await getRegistrationFees(event.id)
      } catch (error) {
        console.error('Failed to fetch registration fees for stall report:', error)
      }
    }

    if (!feeItems || feeItems.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: STALL_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Stall Information')
      return
    }

    //  map of horses by owner ID
    const horsesByOwner: Record<string, IHorseData[]> = {}

    if (horses && horses.length > 0) {
      horses.forEach((horse) => {
        if (horse.horseOwnerId) {
          if (!horsesByOwner[horse.horseOwnerId]) {
            horsesByOwner[horse.horseOwnerId] = []
          }
          horsesByOwner[horse.horseOwnerId].push(horse)
        }
      })
    }

    // map of user data by user ID
    const userMap: Record<string, IUserInterface> = {}

    if (users && users.length > 0) {
      users.forEach((user) => {
        if (user.id) {
          userMap[user.id] = user
        }
      })
    }

    // group fees by userId
    const feesByUser: Record<string, any> = {}

    feeItems.forEach((fee: any) => {
      const userId = fee.userId

      if (userId) {
        // init user data on first encounter
        if (!feesByUser[userId]) {
          const user = userMap[userId]
          const { firstName, lastName } = getFirstAndLastNames(user)
          const formattedName = `${firstName} ${lastName}`.trim() || fee.userName || 'Unknown'

          feesByUser[userId] = {
            userId,
            userName: formattedName,
            stalls: [],
            bedding: [],
            feed: [],
            ownerHorses: horsesByOwner[userId] || [],
          }
        }

        // add fee to appropriate category based on feesItemCategory
        const category = fee.feesItemCategory
        if (category === 'stalls' || category === 'bedding' || category === 'feed') {
          // skip if item is scratched
          if (fee.isScratched) {
            return
          }

          feesByUser[userId][category].push({
            type: fee.feesTitle || 'Unknown',
            count: fee.selectedUnitsCount || 0,
            price: fee.feesPrice || 0,
          })
        }
      }
    })

    // transform aggregated data into rows for the report
    const rows = Object.values(feesByUser)
      .map((userFees: any) => {
        const stallDetails =
          userFees.stalls.map((item: any) => `${item.type}: ${item.count}`).join(', ') || ''

        const beddingDetails =
          userFees.bedding.map((item: any) => `${item.type}: ${item.count}`).join(', ') || ''

        const feedDetails =
          userFees.feed.map((item: any) => `${item.type}: ${item.count}`).join(', ') || ''

        const totalItems =
          userFees.stalls.reduce((sum: number, item: any) => sum + item.count, 0) +
          userFees.bedding.reduce((sum: number, item: any) => sum + item.count, 0) +
          userFees.feed.reduce((sum: number, item: any) => sum + item.count, 0)

        // get horses owned by this user
        let horseInfo = 'No horses'
        if (userFees.ownerHorses.length > 0) {
          const horseNames = userFees.ownerHorses
            .map((horse: IHorseData) => horse.horseName)
            .filter(Boolean) // remove null or undefined vals
            .join(', ')

          horseInfo = horseNames || 'No horses'
        }

        const record: StallReportRow = {} as StallReportRow
        record[riderNameCol] = userFees.userName
        record[horsesCol] = horseInfo
        record[totalItemsCol] = totalItems
        record[stallCol] = stallDetails
        record[beddingCol] = beddingDetails
        record[feedCol] = feedDetails

        return record
      })
      .sort((a, b) => {
        const totalA: number = Number(a[totalItemsCol]) || 0
        const totalB: number = Number(b[totalItemsCol]) || 0
        return totalB - totalA
      })

    if (rows.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: STALL_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Stall Information')
      return
    }

    const ws = XLSX.utils.json_to_sheet(rows, { header: STALL_REPORT_COLUMNS })

    const columnWidths = STALL_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...rows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          25
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Stall Information')
  } catch (err) {
    console.error('Failed to generate stall report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: STALL_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Stall Information')
  }
}

// Generate team report (horizontal format)
const generateTeamReport = async (workbook: XLSX.WorkBook, data: ExportData): Promise<void> => {
  const [roleCol, nameCol, phoneCol, emailCol, paperworkStatusCol, riderCol, horseCol] =
    TEAM_REPORT_COLUMNS

  try {
    const { event } = data
    const eventId = event?.id || ''

    if (!eventId) {
      const ws = XLSX.utils.json_to_sheet([], { header: TEAM_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Team Information')
      return
    }

    const teamReportData = await getTeamReportData(eventId)

    if (!teamReportData || teamReportData.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: TEAM_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Team Information')
      return
    }

    const rows = teamReportData.map((member) => {
      const record: TeamReportRow = {}
      record[roleCol] = member.role || ''
      record[nameCol] = member.name || ''
      record[phoneCol] = member.phone || ''
      record[emailCol] = member.email || ''
      record[paperworkStatusCol] = member.paperworkStatus || ''
      record[riderCol] = member.rider || ''
      record[horseCol] = member.horse || ''

      return record
    })

    // sort the rows by role and then by rider name
    rows.sort((a, b) => {
      const roleCompare = String(a[roleCol]).localeCompare(String(b[roleCol]))
      if (roleCompare !== 0) {
        return roleCompare
      }

      return String(a[riderCol]).localeCompare(String(b[riderCol]))
    })

    const ws = XLSX.utils.json_to_sheet(rows, { header: TEAM_REPORT_COLUMNS })

    const columnWidths = TEAM_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...rows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          15
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Team Information')
  } catch (err) {
    console.error('Failed to generate team report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: TEAM_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Team Information')
  }
}

const generateCustomQuestionsReport = async (
  workbook: XLSX.WorkBook,
  data: ExportData
): Promise<void> => {
  const [
    registrarNameCol,
    teamMembersCol,
    teamNamesCol,
    entryStatusCol,
    horseNamesCol,
    questionCol,
    answerCol,
    emailCol,
    phoneCol,
    messageUserCol,
  ] = CUSTOM_QUESTIONS_REPORT_COLUMNS

  try {
    const { eventRegisteredUsers, registeredUsersByDay, horses, users, event } = data

    if (!eventRegisteredUsers || eventRegisteredUsers.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: CUSTOM_QUESTIONS_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Custom Questions Information')
      return
    }

    //  mapping of question ids to their full text
    const questionMap = new Map<string, string>()
    if (event?.questions && Array.isArray(event.questions)) {
      event.questions.forEach((question) => {
        if (question.id && question.value) {
          questionMap.set(question.id, question.value)
        }
      })
    }

    const userMap = new Map<string, IUserInterface>()
    if (users && users.length > 0) {
      users.forEach((user) => {
        if (user.id) {
          userMap.set(user.id, user)
        }
      })
    }

    const horseMap = new Map<string, string>()
    if (horses && horses.length > 0) {
      horses.forEach((horse) => {
        if (horse.id && horse.horseName) {
          horseMap.set(horse.id, horse.horseName)
        }
      })
    }

    const userRows: Record<string, CustomQuestionsRow[]> = {}
    const userIds: string[] = []
    const userEntryStatus: Record<string, string> = {}

    eventRegisteredUsers.forEach((user) => {
      if (!user.userId) return

      const userId = user.userId

      if (!userRows[userId]) {
        userRows[userId] = []
        userIds.push(userId)
      }

      let userInfo: IUserInterface | undefined
      userInfo = userMap.get(user.userId)

      // common user data
      const registrarName = getFormattedUserName(userInfo) || user.userName || ''
      const firstName = registrarName ? registrarName.split(' ')[0] : 'User'
      const email = userInfo?.userEmail || user.userEmail || ''
      const phone = userInfo?.userPhoneNumber || ''

      // get horse names
      const userHorseNames: string[] = []
      if (user.horsesIds && Array.isArray(user.horsesIds)) {
        user.horsesIds.forEach((horseId) => {
          const horseName = horseMap.get(horseId)
          if (horseName) userHorseNames.push(horseName)
        })
      }

      // team members
      const teamMemberNames: string[] = []
      const teamNames = 'Coming soon'

      if (user.membersIds && Array.isArray(user.membersIds)) {
        user.membersIds.forEach((memberId) => {
          const memberInfo = userMap.get(memberId)
          if (memberInfo) {
            teamMemberNames.push(getFormattedUserName(memberInfo))
          }
        })
      }

      const horseNamesStr = userHorseNames.join(', ')
      const teamMembersStr = teamMemberNames.join(', ')

      // answer map
      const answerMap = new Map<string, string>()
      if (user.answers && Array.isArray(user.answers)) {
        user.answers.forEach((answer) => {
          if (answer.questionId) {
            answerMap.set(answer.questionId, answer.answer || 'Not answered')
          }
        })
      }

      let entryStatus = 'Draft'
      if (user.isRegistered) {
        const rides = registeredUsersByDay?.filter((r) => r.userId === userId) || []
        const allScratched = rides.length > 0 && rides.every((r) => r.isScratched === true)
        entryStatus = allScratched ? 'All Scratched' : 'Active'
      }

      userEntryStatus[userId] = entryStatus

      const rows = Array.from(questionMap.entries()).map(([questionId, questionText], index) => {
        const answer = answerMap.get(questionId) || 'No answer provided'

        const row = {
          [registrarNameCol]: index === 0 ? registrarName : '',
          [teamMembersCol]: index === 0 ? teamMembersStr : '',
          [teamNamesCol]: index === 0 ? teamNames : '',
          [entryStatusCol]: index === 0 ? entryStatus : '',
          [horseNamesCol]: index === 0 ? horseNamesStr : '',
          [questionCol]: questionText,
          [answerCol]: answer,
          [emailCol]: index === 0 ? email : '',
          [phoneCol]: index === 0 ? phone : '',
          [messageUserCol]: index === 0 ? `Message ${firstName}` : '',
        } as CustomQuestionsRow

        return row
      })

      userRows[userId] = rows
    })

    // sort userIds by entry status - active -> all scratched -> draft
    userIds.sort((userIdA, userIdB) => {
      const statusA = userEntryStatus[userIdA]
      const statusB = userEntryStatus[userIdB]

      const statusPriority: Record<string, number> = {
        Active: 1,
        'All Scratched': 2,
        Draft: 3,
      }

      const priorityA: number = statusPriority[statusA] || 4
      const priorityB: number = statusPriority[statusB] || 4

      // sort by priority first
      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }

      return 0
    })

    // flatten rows for the worksheet - keep track of merge ranges
    const finalRows: CustomQuestionsRow[] = []
    const mergeCells: XLSX.Range[] = []
    const hyperlinks: { cell: string; userId: string }[] = []

    let rowIndex = 1 // start after header row

    userIds.forEach((userId) => {
      const rows = userRows[userId]
      if (rows.length === 0) return

      // add all rows for each user
      finalRows.push(...rows)

      // track cells that need hyperlinks - only for the first row of each user
      const messageUserColIndex = CUSTOM_QUESTIONS_REPORT_COLUMNS.indexOf(messageUserCol)
      if (messageUserColIndex >= 0) {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: messageUserColIndex })
        hyperlinks.push({ cell: cellAddress, userId })
      }

      // if user has multiple rows - create merge ranges for user info cols
      if (rows.length > 1) {
        const startRow = rowIndex
        const endRow = rowIndex + rows.length - 1

        // cols to merge (all except question and answer)
        const columnsToMerge = [
          registrarNameCol,
          teamMembersCol,
          teamNamesCol,
          entryStatusCol,
          horseNamesCol,
          emailCol,
          phoneCol,
          messageUserCol,
        ]

        //  merge ranges for each col
        columnsToMerge.forEach((column) => {
          const colIndex = CUSTOM_QUESTIONS_REPORT_COLUMNS.indexOf(column)
          if (colIndex >= 0) {
            mergeCells.push({
              s: { r: startRow, c: colIndex },
              e: { r: endRow, c: colIndex },
            })
          }
        })
      }

      rowIndex += rows.length
    })

    if (finalRows.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: CUSTOM_QUESTIONS_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Custom Questions Information')
      return
    }

    const ws = XLSX.utils.json_to_sheet(finalRows, { header: CUSTOM_QUESTIONS_REPORT_COLUMNS })

    // apply merge ranges
    if (!ws['!merges']) ws['!merges'] = []
    ws['!merges'] = mergeCells

    // add hyperlinks to the worksheet
    hyperlinks.forEach(({ cell, userId }) => {
      const baseUrl = window.location.origin
      const messageUrl = `${baseUrl}/userprofile/${userId}`

      // add hyperlink property to cell
      if (ws[cell]) {
        ws[cell].l = {
          Target: messageUrl,
          Tooltip: `Send a message to this user`,
        }
      }
    })

    const columnWidths = CUSTOM_QUESTIONS_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...finalRows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          20
        ),
      }
    })

    // widths for question and answer columns
    const questionIndex = CUSTOM_QUESTIONS_REPORT_COLUMNS.indexOf(questionCol)
    if (questionIndex >= 0) {
      columnWidths[questionIndex].wch = Math.max(columnWidths[questionIndex].wch, 50)
    }

    const answerIndex = CUSTOM_QUESTIONS_REPORT_COLUMNS.indexOf(answerCol)
    if (answerIndex >= 0) {
      columnWidths[answerIndex].wch = Math.max(columnWidths[answerIndex].wch, 70)
    }

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Custom Questions Information')
  } catch (err) {
    console.error('Failed to generate custom questions report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: CUSTOM_QUESTIONS_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Custom Questions Information')
  }
}

// Generate horse data report (horizontal format)
const generateHorseReport = async (workbook: XLSX.WorkBook, data: ExportData): Promise<void> => {
  const [
    horseNameCol,
    horseBreedCol,
    horseBreederCol,
    horseHeightCol,
    horseYearOfBirthCol,
    horseGenderCol,
    horseGeldingStatusCol,
    horseDisciplineCol,
    horseZoneCol,
    horseColoringCol,
    horseMarkingsCol,
    horseCountryOfOriginCol,
    horseMicrochipNumberCol,
    horseDNACaseNumberCol,
    horsePassportNumberCol,
    horseCogginsCertificateCol,
    horseHealthCertificateCol,
    horseVaccinationCertificateCol,
    horseRhinopneumonitisCertificateCol,
    horseInfluenzaCertificateCol,
    horseSireCol,
    horseDamCol,
    horseGrandSireCol,
    horseGrandDamCol,
    horseMembershipNumbersCol,
  ] = HORSE_REPORT_COLUMNS

  try {
    const { registeredUsersByDay, horses } = data

    if (!registeredUsersByDay?.length || !horses?.length) {
      const ws = XLSX.utils.json_to_sheet([], { header: HORSE_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Horse Information')
      return
    }

    //  unique horse IDs from registered users
    const uniqueHorseIds = new Set<string>()
    registeredUsersByDay.forEach((registration) => {
      if (registration.horsesSelected && registration.horsesSelected.length > 0) {
        registration.horsesSelected.forEach((horse) => {
          if (horse.id && horse.id !== 'No Horse') {
            uniqueHorseIds.add(horse.id)
          }
        })
      }
    })

    //  horses based on unique horse IDs
    const filteredHorses = horses.filter((horse) => horse.id && uniqueHorseIds.has(horse.id))

    const horseIds = filteredHorses.map((horse) => horse.horseId).filter(Boolean) as string[]

    const horsePaperworkMap = await getHorsePaperworkData(horseIds)

    const finalRows: HorseReportRow[] = filteredHorses.map((horse) => {
      const paperwork = horsePaperworkMap.get(horse.horseId || '')

      const row: HorseReportRow = {
        [horseNameCol]: horse.horseName || '',
        [horseBreedCol]:
          horseBreedList
            .filter((item) => horse?.horseBreed?.includes(item.value))
            .map((item) => item.label)
            .join(', ') || '',
        [horseBreederCol]: horse.horseBreeder || '',
        [horseHeightCol]:
          horseHeightList.find((item) => item.value === horse?.horseHeight)?.label || '',
        [horseYearOfBirthCol]: formatHorseYearOfBirth(horse.horseDob) || '',
        [horseGenderCol]: horse.horseGender || '',
        [horseGeldingStatusCol]: horse.horseGeldingStatus || '',
        [horseDisciplineCol]:
          disciplineData.find((item) => item.value === horse?.horseDiscipline)?.label || '',
        [horseZoneCol]: zoneList.find((item) => item.value === horse?.horseZone)?.label || '',
        [horseColoringCol]:
          horseColorList.find((item) => item.value === horse?.horseColor)?.label || '',
        [horseMarkingsCol]:
          horseMarkingList
            .filter((item) => horse?.horseMarking?.includes(item.value))
            .map((item) => item.label)
            .join(', ') || '',
        [horseCountryOfOriginCol]:
          CountryList.find((item) => item.value === horse?.horseCountry)?.label || '',
        [horseMicrochipNumberCol]: horse.horseMicrochipNumber || '',
        [horseDNACaseNumberCol]: horse.horseDNACaseNumber || '',
        [horsePassportNumberCol]: horse.horsePassportNumber || '',
        // Coggins, Health, and Vaccination certificates expire after 12 months
        [horseCogginsCertificateCol]: checkCertificateStatus(paperwork?.cogginsInspectionDate, 12),
        [horseHealthCertificateCol]: checkCertificateStatus(paperwork?.healthInspectionDate, 12),
        [horseVaccinationCertificateCol]: checkCertificateStatus(
          paperwork?.vaccinationInspectionDate,
          12
        ),
        // Rhinopneumonitis and Influenza certificates expire after 6 months
        [horseRhinopneumonitisCertificateCol]: checkCertificateStatus(
          paperwork?.rhinopneumonitisInspectionDate,
          6
        ),
        [horseInfluenzaCertificateCol]: checkCertificateStatus(
          paperwork?.influenzaInspectionDate,
          6
        ),
        [horseSireCol]: 'Coming soon',
        [horseDamCol]: 'Coming soon',
        [horseGrandSireCol]: 'Coming soon',
        [horseGrandDamCol]: 'Coming soon',
        [horseMembershipNumbersCol]: getHorseMembershipNumbers(horse),
      }

      return row
    })

    if (finalRows.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: HORSE_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Horse Information')
      return
    }

    const ws = XLSX.utils.json_to_sheet(finalRows, { header: HORSE_REPORT_COLUMNS })

    const columnWidths = HORSE_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...finalRows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          20
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Horse Information')
  } catch (err) {
    console.error('Failed to generate horse data report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: HORSE_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Horse Information')
  }
}

// Generate rider data report (horizontal format)
const generateRiderReport = async (workbook: XLSX.WorkBook, data: ExportData): Promise<void> => {
  const [
    riderFirstNameCol,
    riderLastNameCol,
    riderNationalityCol,
    backNumberCol,
    noMemberNumberReasonCol,
    riderTrainerNameCol,
    riderOwnerNameCol,
    riderCoachNameCol,
    riderGuardianNameCol,
    riderDisciplineCol,
    riderZoneCol,
    riderAmateurProStatusCol,
    riderJudgingLicenseCol,
    riderForeignEndorsementCol,
    riderEmailCol,
    riderPhoneCol,
    riderStreetAddressCol,
    riderCityCol,
    riderStateCol,
    riderZipCol,
    riderMembershipNumbersCol,
  ] = RIDER_REPORT_COLUMNS

  try {
    const { registeredUsersByDay, users, event } = data

    const eventId = event?.id || ''

    if (!registeredUsersByDay?.length || !users?.length) {
      const ws = XLSX.utils.json_to_sheet([], { header: RIDER_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Rider Information')
      return
    }

    const userMap = new Map<string, IUserInterface>()
    users.forEach((user) => {
      if (user.id) {
        userMap.set(user.id, user)
      }
    })

    const riderTeamMembersMap = await getRiderTeamMemberData(eventId)

    // get unique riders
    const uniqueRegisteredUsers = registeredUsersByDay.reduce<Array<IRegistrationByDayInterface>>(
      (acc, current) => {
        const userId = current.userId

        if (!userId) return acc

        const exists = acc.some((item) => item.userId === userId)

        if (!exists) {
          acc.push(current)
        }

        return acc
      },
      []
    )

    const finalRows: RiderReportRow[] = uniqueRegisteredUsers
      .map((registration) => {
        const userId = registration.userId
        const user = userId ? userMap.get(userId) : undefined

        if (!user) return null

        const { firstName, lastName } = getFirstAndLastNames(user)

        const row: RiderReportRow = {
          [riderFirstNameCol]: firstName,
          [riderLastNameCol]: lastName,
          [riderNationalityCol]:
            CountryList.find((item) => item.value === user?.userNationality)?.label || '',
          [backNumberCol]: registration.backNumber ?? 0,
          [noMemberNumberReasonCol]: '',
          [riderTrainerNameCol]:
            getTeamMemberNamesByRole(userId, 'Trainer', riderTeamMembersMap) || '',
          [riderOwnerNameCol]: getTeamMemberNamesByRole(userId, 'Owner', riderTeamMembersMap) || '',
          [riderCoachNameCol]: getTeamMemberNamesByRole(userId, 'Coach', riderTeamMembersMap) || '',
          [riderGuardianNameCol]:
            getTeamMemberNamesByRole(userId, 'Guardian', riderTeamMembersMap) ||
            user.userGuardian ||
            '',
          [riderDisciplineCol]:
            disciplineData.find((item) => item.value === user?.userDiscipline)?.label || '',
          [riderZoneCol]: user.userZone || '',
          [riderAmateurProStatusCol]:
            amateurList.find((item) => item.value === user.userAmateur)?.label || '',
          [riderJudgingLicenseCol]: user.userJudgingLicense || '',
          [riderForeignEndorsementCol]: user.userForeignEndorsement
            ? `${user.userForeignEndorsement.file ? 'Yes, ' : 'No'} ${user.userForeignEndorsementDate ? formatDate(user.userForeignEndorsementDate) : ''}`
            : '',
          [riderEmailCol]: user.userEmail || '',
          [riderPhoneCol]: user.userPhoneNumber || '',
          [riderStreetAddressCol]: user.userAddress
            ? parseAddress(user.userAddress)?.street || user.userAddress
            : '',
          [riderCityCol]: user.userAddress ? parseAddress(user.userAddress)?.city || '' : '',
          [riderStateCol]: user.userAddress ? parseAddress(user.userAddress)?.state || '' : '',
          [riderZipCol]: user.userAddress ? parseAddress(user.userAddress)?.zip || '' : '',
          [riderMembershipNumbersCol]: getUserDisciplineNumbers(user),
        }

        return row
      })
      .filter(Boolean) as RiderReportRow[]

    if (finalRows.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: RIDER_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Rider Information')
      return
    }

    const ws = XLSX.utils.json_to_sheet(finalRows, { header: RIDER_REPORT_COLUMNS })

    const columnWidths = RIDER_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...finalRows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          20
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Rider Information')
  } catch (err) {
    console.error('Failed to generate rider data report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: RIDER_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Rider Information')
  }
}

export const generateOwnerReport = async (
  workbook: XLSX.WorkBook,
  data: ExportData
): Promise<void> => {
  const [
    ownerNameCol,
    horseNameCol,
    riderNameCol,
    ownerEmailCol,
    ownerPhoneCol,
    ownerStreetAddressCol,
    ownerCityCol,
    ownerStateCol,
    ownerZipCol,
    ownerNationalityCol,
    ownerMembershipNumbersCol,
  ] = OWNER_REPORT_COLUMNS

  try {
    const { users, event, horses, registeredUsersByDay } = data

    if (!users || users.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: OWNER_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Owner Information')
      return
    }

    const userMap = new Map<string, IUserInterface>()
    users.forEach((user) => {
      if (user.id) {
        userMap.set(user.id, user)
      }
    })

    const eventId = event?.id
    if (!eventId) {
      console.error('No event ID found')
      const ws = XLSX.utils.json_to_sheet([], { header: OWNER_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Owner Information')
      return
    }

    // get the owner ids for this event
    const ownerIds = await getRoleIds(eventId, 'Owner')

    // owner to horses mapping
    const ownerHorsesMap = new Map<string, { id: string; name: string }[]>()

    // for each owner find all their horses
    if (horses && horses.length > 0) {
      ownerIds.forEach((ownerId) => {
        // find all horses owned by this owner
        const ownedHorses = horses
          .filter((horse) => horse.horseOwnerId === ownerId && horse.id)
          .map((horse) => ({ id: horse.id as string, name: horse.horseName || '' }))

        if (ownedHorses.length > 0) {
          ownerHorsesMap.set(ownerId, ownedHorses)
        }
      })
    }

    //  find riders for each owner's horses
    const ownerHorseRiderRows: OwnerReportRow[] = []

    // set of unique combinations of owner - rider - horse
    const uniqueCombinations = new Set<string>()

    for (const ownerId of ownerIds) {
      const owner = userMap.get(ownerId)

      if (!owner) continue

      const { firstName, lastName } = getFirstAndLastNames(owner)
      const fullName = `${firstName} ${lastName}`.trim()

      //  base owner row data
      const baseOwnerRow = {
        [ownerNameCol]: fullName,
        [ownerEmailCol]: owner.userEmail || '',
        [ownerPhoneCol]: owner.userPhoneNumber || '',
        [ownerStreetAddressCol]: owner.userAddress
          ? parseAddress(owner.userAddress)?.street || owner.userAddress
          : '',
        [ownerCityCol]: owner.userAddress ? parseAddress(owner.userAddress)?.city || '' : '',
        [ownerStateCol]: owner.userAddress ? parseAddress(owner.userAddress)?.state || '' : '',
        [ownerZipCol]: owner.userAddress ? parseAddress(owner.userAddress)?.zip || '' : '',
        [ownerNationalityCol]:
          CountryList.find((item) => item.value === owner?.userNationality)?.label || '',

        [ownerMembershipNumbersCol]: getUserDisciplineNumbers(owner),
      }

      // get the horses for this owner
      const ownedHorses = ownerHorsesMap.get(ownerId) || []

      // if owner has no horses - row with just the owner info
      if (ownedHorses.length === 0) {
        // combination key for owner only row
        const combinationKey = `${ownerId}||`

        if (!uniqueCombinations.has(combinationKey)) {
          uniqueCombinations.add(combinationKey)

          ownerHorseRiderRows.push({
            ...baseOwnerRow,
            [riderNameCol]: '',
            [horseNameCol]: '',
          })
        }
        continue
      }

      // for each horse find all riders
      let horseRiderFound = false

      for (const horse of ownedHorses) {
        let riderFound = false

        // loop registrations to find riders of this horse
        if (registeredUsersByDay && registeredUsersByDay.length > 0) {
          for (const registration of registeredUsersByDay) {
            if (
              !registration.riderId ||
              !registration.horsesSelected ||
              registration.horsesSelected.length === 0
            )
              continue

            // check if this registration includes the current horse
            if (registration.horsesSelected.find((item) => item.id === horse.id)) {
              // unique key for this combination
              const combinationKey = `${ownerId}|${horse.id}|${registration.riderId}`

              if (!uniqueCombinations.has(combinationKey)) {
                uniqueCombinations.add(combinationKey)
                // add row for this owner-horse-rider combination
                ownerHorseRiderRows.push({
                  ...baseOwnerRow,
                  [riderNameCol]: registration.riderName || '',
                  [horseNameCol]: horse.name,
                })
              }

              riderFound = true
              horseRiderFound = true
            }
          }
        }

        // if no rider found for this horse, still add a row with the horse but no rider
        if (!riderFound) {
          // unique key for owner-horse (no rider) combination
          const combinationKey = `${ownerId}|${horse.id}|`

          if (!uniqueCombinations.has(combinationKey)) {
            uniqueCombinations.add(combinationKey)

            ownerHorseRiderRows.push({
              ...baseOwnerRow,
              [riderNameCol]: '',
              [horseNameCol]: horse.name,
            })
          }
          horseRiderFound = true
        }
      }

      // if no horses or riders were found the owner - add one row with just owner info
      if (!horseRiderFound) {
        // unique key for owner-only row
        const combinationKey = `${ownerId}||`

        if (!uniqueCombinations.has(combinationKey)) {
          uniqueCombinations.add(combinationKey)

          ownerHorseRiderRows.push({
            ...baseOwnerRow,
            [riderNameCol]: '',
            [horseNameCol]: '',
          })
        }
      }
    }

    if (ownerHorseRiderRows.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: OWNER_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Owner Information')
      return
    }

    const ws = XLSX.utils.json_to_sheet(ownerHorseRiderRows, { header: OWNER_REPORT_COLUMNS })

    const columnWidths = OWNER_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...ownerHorseRiderRows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          20
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Owner Information')
  } catch (err) {
    console.error('Failed to generate owner data report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: OWNER_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Owner Information')
  }
}

export const generateTrainerReport = async (
  workbook: XLSX.WorkBook,
  data: ExportData
): Promise<void> => {
  const [
    trainerNameCol,
    horseNameCol,
    riderNameCol,
    trainerEmailCol,
    trainerPhoneCol,
    trainerStreetAddressCol,
    trainerCityCol,
    trainerStateCol,
    trainerZipCol,
    trainerNationalityCol,
    trainerMembershipNumbersCol,
  ] = TRAINER_REPORT_COLUMNS

  try {
    const { users, event, horses, registeredUsersByDay } = data

    if (!users || users.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: TRAINER_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Trainer Information')
      return
    }

    const userMap = new Map<string, IUserInterface>()
    users.forEach((user) => {
      if (user.id) {
        userMap.set(user.id, user)
      }
    })

    const eventId = event?.id
    if (!eventId) {
      console.error('No event ID found')
      const ws = XLSX.utils.json_to_sheet([], { header: TRAINER_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Trainer Information')
      return
    }

    const trainerRidersMap = await getTrainerRiderRelationships(eventId)

    // unique combinations
    const uniqueCombinations = new Set<string>()

    const finalRows: TrainerReportRow[] = []

    const trainerIds = Array.from(trainerRidersMap.keys())

    for (const trainerId of trainerIds) {
      const trainer = userMap.get(trainerId)
      if (!trainer) continue

      const { firstName, lastName } = getFirstAndLastNames(trainer)
      const fullName = `${firstName} ${lastName}`.trim()

      // base trainer row data
      const baseTrainerRow = {
        [trainerNameCol]: fullName,
        [trainerEmailCol]: trainer.userEmail || '',
        [trainerPhoneCol]: trainer.userPhoneNumber || '',
        [trainerStreetAddressCol]: trainer.userAddress
          ? parseAddress(trainer.userAddress)?.street || trainer.userAddress
          : '',
        [trainerCityCol]: trainer.userAddress ? parseAddress(trainer.userAddress)?.city || '' : '',
        [trainerStateCol]: trainer.userAddress
          ? parseAddress(trainer.userAddress)?.state || ''
          : '',
        [trainerZipCol]: trainer.userAddress ? parseAddress(trainer.userAddress)?.zip || '' : '',
        [trainerNationalityCol]:
          CountryList.find((item) => item.value === trainer?.userNationality)?.label || '',
        [trainerMembershipNumbersCol]: getUserDisciplineNumbers(trainer),
      }

      const riders = trainerRidersMap.get(trainerId) || []

      if (riders.length === 0) {
        // if trainer has no riders - add a row with just the trainer info
        const combinationKey = `${trainerId}||`
        if (!uniqueCombinations.has(combinationKey)) {
          uniqueCombinations.add(combinationKey)

          finalRows.push({
            ...baseTrainerRow,
            [riderNameCol]: '',
            [horseNameCol]: '',
          })
        }
        continue
      }

      let riderHorseFound = false

      for (const rider of riders) {
        let horseFound = false

        if (registeredUsersByDay && registeredUsersByDay.length > 0) {
          for (const registration of registeredUsersByDay) {
            if (
              registration.riderId !== rider.riderId ||
              !registration.horsesSelected ||
              registration.horsesSelected.length === 0
            ) {
              continue
            }

            for (const current of registration.horsesSelected) {
              if (!current.id || current.id === 'No Horse') continue

              const horse = horses?.find((h) => h.id === current.id)
              if (!horse) continue

              const horseName = horse.horseName || ''

              // unique key for this combination
              const combinationKey = `${trainerId}|${rider.riderId}|${horse.horseId}`

              if (!uniqueCombinations.has(combinationKey)) {
                uniqueCombinations.add(combinationKey)

                finalRows.push({
                  ...baseTrainerRow,
                  [riderNameCol]: rider.riderName,
                  [horseNameCol]: horseName,
                })
              }

              horseFound = true
              riderHorseFound = true
            }
          }
        }

        // if rider has no horses - add a row with the rider but no horse
        if (!horseFound) {
          const combinationKey = `${trainerId}|${rider.riderId}|`

          if (!uniqueCombinations.has(combinationKey)) {
            uniqueCombinations.add(combinationKey)

            finalRows.push({
              ...baseTrainerRow,
              [riderNameCol]: rider.riderName,
              [horseNameCol]: '',
            })
          }

          riderHorseFound = true
        }
      }

      // if trainer has no riders or horses - add a row with just trainer info
      if (!riderHorseFound) {
        const combinationKey = `${trainerId}||`

        if (!uniqueCombinations.has(combinationKey)) {
          uniqueCombinations.add(combinationKey)

          finalRows.push({
            ...baseTrainerRow,
            [riderNameCol]: '',
            [horseNameCol]: '',
          })
        }
      }
    }

    if (finalRows.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: TRAINER_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Trainer Information')
      return
    }

    const ws = XLSX.utils.json_to_sheet(finalRows, { header: TRAINER_REPORT_COLUMNS })

    const columnWidths = TRAINER_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...finalRows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          20
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Trainer Information')
  } catch (err) {
    console.error('Failed to generate trainer data report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: TRAINER_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Trainer Information')
  }
}

export const generateGuardianReport = async (
  workbook: XLSX.WorkBook,
  data: ExportData
): Promise<void> => {
  const [
    guardianNameCol,
    minorCol,
    guardianEmailCol,
    guardianPhoneCol,
    guardianAddressCol,
    guardianCityCol,
    guardianStateCol,
    guardianZipCol,
    guardianNationalityCol,
    guardianMembershipNumbersCol,
    paperworkStatusCol,
  ] = GUARDIAN_REPORT_COLUMNS

  try {
    const { users, event } = data
    if (!users || users.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: GUARDIAN_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Guardian Information')
      return
    }

    const userMap = new Map<string, IUserInterface>()
    users.forEach((user) => {
      if (user.id) {
        userMap.set(user.id, user)
      }
    })

    const eventId = event?.id
    if (!eventId) {
      console.error('No event ID found')
      const ws = XLSX.utils.json_to_sheet([], { header: GUARDIAN_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Guardian Information')
      return
    }

    const guardianToRidersMap = await getGuardianToRidersMap(eventId)
    const guardianIds = Array.from(guardianToRidersMap.keys())

    const teamReportData = await getTeamReportData(eventId)

    //  map of rider name to paperwork status
    const riderPaperworkMap = new Map<string, string>()
    teamReportData.forEach((teamMember) => {
      if (teamMember.rider && teamMember.paperworkStatus) {
        riderPaperworkMap.set(teamMember.rider, teamMember.paperworkStatus)
      }
    })

    const guardianRows: Record<string, GuardianReportRow[]> = {}
    const guardianIdsWithRows: string[] = []

    // process each guardian
    for (const guardianId of guardianIds) {
      const guardian = userMap.get(guardianId)
      if (!guardian) continue

      const riderIds = guardianToRidersMap.get(guardianId) || []
      if (riderIds.length === 0) continue

      guardianRows[guardianId] = []
      guardianIdsWithRows.push(guardianId)

      const { firstName, lastName } = getFirstAndLastNames(guardian)
      const fullName = `${firstName} ${lastName}`.trim()

      // common guardian data
      const guardianData = {
        name: fullName,
        email: guardian.userEmail || '',
        phone: guardian.userPhoneNumber || '',
        address: guardian.userAddress
          ? parseAddress(guardian.userAddress)?.street || guardian.userAddress
          : '',
        city: guardian.userAddress ? parseAddress(guardian.userAddress)?.city || '' : '',
        state: guardian.userAddress ? parseAddress(guardian.userAddress)?.state || '' : '',
        zip: guardian.userAddress ? parseAddress(guardian.userAddress)?.zip || '' : '',
        nationality:
          CountryList.find((item) => item.value === guardian?.userNationality)?.label || '',
        membershipNumbers: getUserDisciplineNumbers(guardian),
      }

      // row for each minor if a guardian has multiple minors
      for (let i = 0; i < riderIds.length; i++) {
        const riderId = riderIds[i]
        const rider = userMap.get(riderId)

        if (!rider) continue

        const { firstName: riderFirstName, lastName: riderLastName } = getFirstAndLastNames(rider)
        const riderName = `${riderFirstName} ${riderLastName}`.trim()

        const paperworkStatus = riderPaperworkMap.get(riderName) || ''

        const row: GuardianReportRow = {
          [guardianNameCol]: i === 0 ? guardianData.name : '', // only show on first row
          [minorCol]: riderName,
          [guardianEmailCol]: i === 0 ? guardianData.email : '',
          [guardianPhoneCol]: i === 0 ? guardianData.phone : '',
          [guardianAddressCol]: i === 0 ? guardianData.address : '',
          [guardianCityCol]: i === 0 ? guardianData.city : '',
          [guardianStateCol]: i === 0 ? guardianData.state : '',
          [guardianZipCol]: i === 0 ? guardianData.zip : '',
          [guardianNationalityCol]: i === 0 ? guardianData.nationality : '',
          [guardianMembershipNumbersCol]: i === 0 ? guardianData.membershipNumbers : '',
          [paperworkStatusCol]: paperworkStatus,
        }

        guardianRows[guardianId].push(row)
      }
    }

    // flatten rows for the worksheet and create merge ranges
    const finalRows: GuardianReportRow[] = []
    const mergeCells: XLSX.Range[] = []
    let rowIndex = 1 // start after header row

    guardianIdsWithRows.forEach((guardianId) => {
      const rows = guardianRows[guardianId]
      if (rows.length === 0) return

      // add all rows for this guardian
      finalRows.push(...rows)

      // If guardian has multiple minors - create merge ranges for guardian info columns
      if (rows.length > 1) {
        const startRow = rowIndex
        const endRow = rowIndex + rows.length - 1

        // columns to merge (all guardian info except minor name and paperwork status)
        const columnsToMerge = [
          guardianNameCol,
          guardianEmailCol,
          guardianPhoneCol,
          guardianAddressCol,
          guardianCityCol,
          guardianStateCol,
          guardianZipCol,
          guardianNationalityCol,
          guardianMembershipNumbersCol,
        ]

        // create merge ranges for each column
        columnsToMerge.forEach((column) => {
          const colIndex = GUARDIAN_REPORT_COLUMNS.indexOf(column)
          if (colIndex >= 0) {
            mergeCells.push({
              s: { r: startRow, c: colIndex },
              e: { r: endRow, c: colIndex },
            })
          }
        })
      }

      rowIndex += rows.length
    })

    if (finalRows.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: GUARDIAN_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Guardian Information')
      return
    }

    const ws = XLSX.utils.json_to_sheet(finalRows, { header: GUARDIAN_REPORT_COLUMNS })

    // apply merge ranges
    if (!ws['!merges']) ws['!merges'] = []
    ws['!merges'] = mergeCells

    const columnWidths = GUARDIAN_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...finalRows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          20
        ),
      }
    })

    ws['!cols'] = columnWidths
    XLSX.utils.book_append_sheet(workbook, ws, 'Guardian Information')
  } catch (err) {
    console.error('Failed to generate guardian data report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: GUARDIAN_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Guardian Information')
  }
}

export const generateJudgeReport = (workbook: XLSX.WorkBook, data: ExportData): void => {
  try {
    const { registeredUsersByDay, users } = data

    if (!registeredUsersByDay?.length || !users?.length) {
      const ws = XLSX.utils.json_to_sheet([], { header: JUDGE_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Detailed Judge Information')
      return
    }

    const userMap = new Map<string, IUserInterface>()
    users.forEach((user) => {
      if (user.id) {
        userMap.set(user.id, user)
      }
    })

    const finalRows: JudgeReportRow[] = registeredUsersByDay
      .filter((registration) => !registration.isScratched)
      .sort((a, b) => {
        const classNameA = a.registrationByDayName || ''
        const classNameB = b.registrationByDayName || ''
        return classNameA.localeCompare(classNameB)
      })
      .map((registration) => {
        // row with base information
        const baseRow: JudgeReportRow = {
          'Class Name': registration.registrationByDayName || '',
          'Rider Name': registration.riderName || '',
          'Horse Name': registration.horseName || '',
        }

        // process judges data if available
        if (registration.score?.judges && registration.score.judges.length > 0) {
          registration.score.judges.forEach((judge, index) => {
            if (index >= 7) return // limit to 7 judges as per column definitions

            const judgeNumber = index + 1
            const judgePrefix = `Judge ${judgeNumber} `

            const judgeUser = userMap.get(judge.id)

            const { firstName, lastName } = judgeUser
              ? getFirstAndLastNames(judgeUser)
              : { firstName: '', lastName: '' }

            const { percentage, score } = calculateJudgeMetrics(judge)

            // populate judge specific columns
            baseRow[`${judgePrefix}First Name`] = firstName
            baseRow[`${judgePrefix}Last Name`] = lastName
            baseRow[`Judging License`] = judgeUser?.userJudgingLicense
              ? getStringValue(judgeUser?.userJudgingLicense)
              : ''
            baseRow[`${judgePrefix}Percentage`] = percentage
            baseRow[`${judgePrefix}Score (Optional Except Dressage)`] = score

            const isSigned = judge.signatureUrl || judge.signatureDate
            const signatureDate = judge.signatureDate ? new Date(judge.signatureDate) : null

            if (isSigned && signatureDate) {
              const formattedDate = `${signatureDate.getMonth() + 1}/${signatureDate.getDate()}/${signatureDate.getFullYear()}`
              baseRow[`Score Card Signed?`] = `Signed, ${formattedDate}`
            } else if (isSigned) {
              baseRow[`Score Card Signed?`] = `Signed`
            } else {
              baseRow[`Score Card Signed?`] = 'Not yet signed'
            }

            baseRow[`${judgePrefix}Email`] = judgeUser?.userEmail || ''
            baseRow[`${judgePrefix}Phone Number`] = judgeUser?.userPhoneNumber || ''
            baseRow[`${judgePrefix}Nationality`] = judgeUser?.userNationality || ''
            baseRow[`${judgePrefix}Membership Numbers`] = judgeUser
              ? getUserDisciplineNumbers(judgeUser)
              : ''
          })
        } else {
          // if no judges - still add judge specific cols w/ empty values
          for (let j = 1; j <= 7; j++) {
            const judgePrefix = `Judge ${j} `
            baseRow[`${judgePrefix}First Name`] = ''
            baseRow[`${judgePrefix}Last Name`] = ''
            baseRow[`${judgePrefix}Percentage`] = ''
            baseRow[`${judgePrefix}Score (Optional Except Dressage)`] = ''
            baseRow[`${judgePrefix}Email`] = ''
            baseRow[`${judgePrefix}Phone Number`] = ''
            baseRow[`${judgePrefix}Nationality`] = ''
            baseRow[`${judgePrefix}Membership Numbers`] = ''
          }
          baseRow[`Judging License`] = ''
          baseRow[`Score Card Signed?`] = ''
        }

        // make sure all columns are populated
        JUDGE_REPORT_COLUMNS.forEach((column) => {
          if (baseRow[column] === undefined) {
            baseRow[column] = ''
          }
        })

        return baseRow
      })

    if (finalRows.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: JUDGE_REPORT_COLUMNS })
      XLSX.utils.book_append_sheet(workbook, ws, 'Judge Information')
      return
    }

    const ws = XLSX.utils.json_to_sheet(finalRows, { header: JUDGE_REPORT_COLUMNS })

    const columnWidths = JUDGE_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...finalRows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          20
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Judge Information')
  } catch (err) {
    console.error('Failed to generate judge data report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: JUDGE_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Judge Information')
  }
}

export const generateStaffReport = async (
  workbook: XLSX.WorkBook,
  data: ExportData
): Promise<void> => {
  const [
    staffTitleCol,
    staffFirstNameCol,
    staffLastNameCol,
    staffPhoneCol,
    staffEmailCol,
    staffMembershipNumberCol,
  ] = STAFF_REPORT_COLUMNS

  try {
    const { users, event } = data

    if (!users || users.length === 0 || !event?.id) {
      const ws = XLSX.utils.json_to_sheet([], { header: STAFF_REPORT_COLUMNS })

      const columnWidths = STAFF_REPORT_COLUMNS.map((header) => ({
        wch: Math.max(header.length, 20),
      }))

      ws['!cols'] = columnWidths
      XLSX.utils.book_append_sheet(workbook, ws, 'Staff Information')
      return
    }

    const staffRolesData = await getStaffRolesData(event.id)

    if (!staffRolesData || staffRolesData.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: STAFF_REPORT_COLUMNS })

      const columnWidths = STAFF_REPORT_COLUMNS.map((header) => ({
        wch: Math.max(header.length, 20),
      }))

      ws['!cols'] = columnWidths
      XLSX.utils.book_append_sheet(workbook, ws, 'Staff Information')
      return
    }

    const userMap = new Map<string, IUserInterface>()
    users.forEach((user) => {
      if (user.id) {
        userMap.set(user.id, user)
      }
    })

    const finalRows: StaffReportRow[] = []

    staffRolesData.forEach((staffRole) => {
      if (!staffRole.staffId) return

      if (staffRole.isScratched) return

      const staffMember = userMap.get(staffRole.staffId)

      if (!staffMember) return

      const { firstName, lastName } = getFirstAndLastNames(staffMember)

      const row: StaffReportRow = {
        [staffTitleCol]: staffRole.staffRole || 'Staff',
        [staffFirstNameCol]: firstName,
        [staffLastNameCol]: lastName,
        [staffPhoneCol]: staffMember.userPhoneNumber || '',
        [staffEmailCol]: staffMember.userEmail || '',
        [staffMembershipNumberCol]: getUserDisciplineNumbers(staffMember),
      }

      finalRows.push(row)
    })

    if (finalRows.length === 0) {
      const ws = XLSX.utils.json_to_sheet([], { header: STAFF_REPORT_COLUMNS })

      const columnWidths = STAFF_REPORT_COLUMNS.map((header) => ({
        wch: Math.max(header.length, 20),
      }))

      ws['!cols'] = columnWidths
      XLSX.utils.book_append_sheet(workbook, ws, 'Staff Information')
      return
    }

    const ws = XLSX.utils.json_to_sheet(finalRows, { header: STAFF_REPORT_COLUMNS })

    const columnWidths = STAFF_REPORT_COLUMNS.map((header) => {
      return {
        wch: Math.max(
          header.length,
          ...finalRows.map((record) =>
            record[header] !== undefined ? String(record[header]).length : 0
          ),
          20
        ),
      }
    })

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Staff Information')
  } catch (err) {
    console.error('Failed to generate staff report:', err)

    const ws = XLSX.utils.json_to_sheet([], { header: STAFF_REPORT_COLUMNS })

    const columnWidths = STAFF_REPORT_COLUMNS.map((header) => ({
      wch: Math.max(header.length, 20),
    }))

    ws['!cols'] = columnWidths
    XLSX.utils.book_append_sheet(workbook, ws, 'Staff Information')
  }
}

export const generateSponsorReport = (workbook: XLSX.WorkBook, data: ExportData): void => {
  const [sponsorNameCol] = SPONSOR_REPORT_COLUMNS

  try {
    const comingSoonRow: SponsorReportRow = {} as SponsorReportRow
    comingSoonRow[sponsorNameCol] = 'Coming soon'

    const ws = XLSX.utils.json_to_sheet([comingSoonRow], { header: SPONSOR_REPORT_COLUMNS })

    const columnWidths = SPONSOR_REPORT_COLUMNS.map((header) => ({
      wch: Math.max(header.length, 20),
    }))

    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(workbook, ws, 'Sponsor Information')
  } catch (err) {
    console.error('Failed to generate sponsor report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: SPONSOR_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Sponsor Information')
  }
}

export const generateOnsiteVendorReport = (workbook: XLSX.WorkBook, data: ExportData): void => {
  const [onsiteVendorNameCol] = ONSITE_VENDOR_REPORT_COLUMNS
  try {
    const comingSoonRow: OnsiteVendorReportRow = {} as OnsiteVendorReportRow
    comingSoonRow[onsiteVendorNameCol] = 'Coming soon'

    const ws = XLSX.utils.json_to_sheet([comingSoonRow], { header: ONSITE_VENDOR_REPORT_COLUMNS })

    const columnWidths = ONSITE_VENDOR_REPORT_COLUMNS.map((header) => ({
      wch: Math.max(header.length, 20),
    }))

    ws['!cols'] = columnWidths

    ws['A2'] = { v: 'Coming soon', t: 's' }

    XLSX.utils.book_append_sheet(workbook, ws, 'Onsite Vendor Information')
  } catch (err) {
    console.error('Failed to generate onsite vendor report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: ONSITE_VENDOR_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Onsite Vendor Information')
  }
}

export const generateVirtualVendorReport = (workbook: XLSX.WorkBook, data: ExportData): void => {
  const [virtualVendorNameCol] = VIRTUAL_VENDOR_REPORT_COLUMNS
  try {
    const comingSoonRow: VirtualVendorReportRow = {} as VirtualVendorReportRow
    comingSoonRow[virtualVendorNameCol] = 'Coming soon'

    const ws = XLSX.utils.json_to_sheet([comingSoonRow], { header: VIRTUAL_VENDOR_REPORT_COLUMNS })

    const columnWidths = VIRTUAL_VENDOR_REPORT_COLUMNS.map((header) => ({
      wch: Math.max(header.length, 20),
    }))

    ws['!cols'] = columnWidths

    ws['A2'] = { v: 'Coming soon', t: 's' }

    XLSX.utils.book_append_sheet(workbook, ws, 'Virtual Vendor Information')
  } catch (err) {
    console.error('Failed to generate virtual vendor report:', err)
    const ws = XLSX.utils.json_to_sheet([], { header: VIRTUAL_VENDOR_REPORT_COLUMNS })
    XLSX.utils.book_append_sheet(workbook, ws, 'Virtual Vendor Information')
  }
}

export const exportDocument = async (
  data: ExportData,
  enabledOptions: EnabledOptionsType,
  fileName: string
): Promise<void> => {
  const workbook = await generateExport(data, enabledOptions)
  XLSX.writeFile(workbook, fileName)
}
